/**
 * Job Application Status Enum
 * Belongs to JobApplicant entity
 *
 * NOTE:
 * OnboardingDone and Hired are intentionally kept as separate statuses.
 * Reason:
 * - Onboarding is a multi-step process (documents, system access, profile setup, etc.)
 * - HR may complete onboarding steps but forget or delay creating the final Job / Offer entry
 * - If the process stops midway, we must persist that state and allow HR to continue later
 * - Hired means the process is fully finalized and no further action is required
 */
export const JobApplicationStatusEnum = Object.freeze({
  Applied: 0, // Applicant applied for the position
  ShortList: 1, // HR shortlisted the applicant and sent login credentials
  TermsAccepted: 2, // Applicant accepted terms after receiving credentials
  InterviewTimeSchedule: 3, // HR proposed interview time slots
  Interview: 4, // Applicant selected an interview slot
  FeedbackAdded: 5, // HR added interview feedback
  DocumentSend: 6, // HR sent contract documents
  DocumentRecieved: 7, // Applicant returned signed documents
  OnboardingDone: 8, // HR completed onboarding steps (can be partial/final)
  Hired: 9, // Job offer created and applicant officially hired (final state)
  Reject: 10, // Applicant rejected at any stage (final state)
});

// =======================
// Labels for UI Display
// =======================
export const JobApplicationStatusLabel = Object.freeze({
  [JobApplicationStatusEnum.Applied]: "Applied",
  [JobApplicationStatusEnum.ShortList]: "Shortlisted",
  [JobApplicationStatusEnum.TermsAccepted]: "Credentials Accepted",
  [JobApplicationStatusEnum.InterviewTimeSchedule]: "Interview Scheduling",
  [JobApplicationStatusEnum.Interview]: "Interview Scheduled",
  [JobApplicationStatusEnum.FeedbackAdded]: "Feedback Added",
  [JobApplicationStatusEnum.DocumentSend]: "Documents Sent",
  [JobApplicationStatusEnum.DocumentRecieved]: "Documents Received",
  [JobApplicationStatusEnum.OnboardingDone]: "Onboarding Completed",
  [JobApplicationStatusEnum.Hired]: "Hired",
  [JobApplicationStatusEnum.Reject]: "Rejected",
});

// =====================================
// Muted / Helper Text for Status Detail
// =====================================
export const JobApplicationStatusText = Object.freeze({
  [JobApplicationStatusEnum.Applied]:
    "The applicant has submitted an application for the position.",

  [JobApplicationStatusEnum.ShortList]:
    "HR reviewed and shortlisted the applicant and sent login credentials.",

  [JobApplicationStatusEnum.TermsAccepted]:
    "The applicant received login credentials and accepted the hiring terms.",

  [JobApplicationStatusEnum.InterviewTimeSchedule]:
    "HR proposed multiple interview time slots. The applicant has not selected one yet.",

  [JobApplicationStatusEnum.Interview]:
    "The applicant selected an interview slot and the interview is scheduled.",

  [JobApplicationStatusEnum.FeedbackAdded]: "HR has added interview feedback after the interview.",

  [JobApplicationStatusEnum.DocumentSend]:
    "HR sent contract documents to the applicant for review and signature.",

  [JobApplicationStatusEnum.DocumentRecieved]:
    "The applicant returned the signed contract documents to HR.",

  [JobApplicationStatusEnum.OnboardingDone]:
    "HR completed onboarding steps. The process may still be pending final job creation.",

  [JobApplicationStatusEnum.Hired]:
    "The applicant has been officially hired. Job offer is finalized and no further actions are required.",

  [JobApplicationStatusEnum.Reject]: "The applicant was rejected during the hiring process.",
});

export const JOB_STATUS_CONFIG = {
  [JobApplicationStatusEnum.Applied]: {
    key: "applied",
    label: JobApplicationStatusLabel[0],
  },
  [JobApplicationStatusEnum.ShortList]: {
    key: "shortList",
    label: JobApplicationStatusLabel[1],
  },
  [JobApplicationStatusEnum.TermsAccepted]: {
    key: "termsAccepted",
    label: JobApplicationStatusLabel[2],
  },
  [JobApplicationStatusEnum.InterviewTimeSchedule]: {
    key: "interviewTimeSchedule",
    label: JobApplicationStatusLabel[3],
  },
  [JobApplicationStatusEnum.Interview]: {
    key: "interview",
    label: JobApplicationStatusLabel[4],
  },
  [JobApplicationStatusEnum.FeedbackAdded]: {
    key: "feedbackAdded",
    label: JobApplicationStatusLabel[5],
  },
  [JobApplicationStatusEnum.DocumentSend]: {
    key: "documentSend",
    label: JobApplicationStatusLabel[6],
  },
  [JobApplicationStatusEnum.DocumentRecieved]: {
    key: "documentRecieved",
    label: JobApplicationStatusLabel[7],
  },
  [JobApplicationStatusEnum.OnboardingDone]: {
    key: "onboardingDone",
    label: JobApplicationStatusLabel[8],
  },
  [JobApplicationStatusEnum.Hired]: {
    key: "hired",
    label: JobApplicationStatusLabel[9],
  },
  [JobApplicationStatusEnum.Reject]: {
    key: "reject",
    label: JobApplicationStatusLabel[10],
  },
};

